"use client";

import { useEffect, useState } from "react";
import { useImageStore } from "@/stores/imageStore";
import { usePermissions } from "@/hooks/usePermissions";
import { PermissionSubject } from "@/utils/permissions";
import PermissionGuard from "@/components/PermissionGuard";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { RefreshCw, Plus, Search } from "lucide-react";
import ModalUploadImage from "@/components/image/ModalUploadImage";
import ModalEditImage from "@/components/image/ModalEditImage";
import ModalDeleteImage from "@/components/image/ModalDeleteImage";

export default function ImagePage() {
  const { items, pagination, isLoading, fetch, fetchMore } = useImageStore();
  const { canRead } = usePermissions();

  const [search, setSearch] = useState("");
  // Search only; upload/create handled via modal
  const [openUpload, setOpenUpload] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const canAccessPage = canRead(PermissionSubject.IMAGE);

  useEffect(() => {
    if (canAccessPage) fetch({ page: 1, limit: 24, search });
  }, [canAccessPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetch({ page: 1, limit: 24, search });
  };

  // Infinite scroll
  useEffect(() => {
    const onScroll = () => {
      const nearBottom =
        window.innerHeight + window.scrollY >= document.body.offsetHeight - 200;
      if (nearBottom && !isLoading) {
        fetchMore({ search });
      }
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [isLoading, search, pagination.page]);

  // Upload/Create handled inside modal components

  if (!canAccessPage) {
    return (
      <DashboardLayout>
        <div className="p-10 text-center">
          <div className="text-red-600 text-lg font-medium mb-2">
            Không có quyền truy cập
          </div>
          <div className="text-gray-500">
            Bạn không có quyền truy cập trang quản lý hình ảnh
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex flex-col justify-between gap-5 lg:flex-row lg:items-center">
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-blue-600 via-violet-600 to-fuchsia-600">
              Quản lý hình ảnh
            </h1>
            <div className="mt-2 flex items-center gap-2">
              <p className="text-gray-500">
                Thư viện ảnh dạng lưới, chọn nhanh và thao tác tiện
              </p>
              <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200">
                {pagination.total} ảnh
              </span>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() =>
                fetch({
                  page: pagination.page,
                  limit: pagination.limit,
                  search,
                })
              }
              variant="outline"
              className="flex items-center gap-2 hover:cursor-pointer"
            >
              <RefreshCw
                className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
              />
              Làm mới
            </Button>
            <PermissionGuard subject={PermissionSubject.IMAGE} action="create">
              <Button
                onClick={() => setOpenUpload(true)}
                className="hover:cursor-pointer flex items-center gap-2"
              >
                <Plus className="h-4 w-4" /> Thêm mới ảnh
              </Button>
            </PermissionGuard>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <form
            onSubmit={handleSearch}
            className="grid grid-cols-1 md:grid-cols-6 gap-3"
          >
            <div className="md:col-span-5 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Tìm kiếm theo tiêu đề/alt/caption..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-10"
              />
            </div>
            <div className="md:col-span-1 flex items-stretch justify-end">
              <Button
                type="submit"
                className="w-full h-10 hover:cursor-pointer"
              >
                Tìm kiếm
              </Button>
            </div>
          </form>
        </div>

        {/* Gallery */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Đang tải...</span>
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center text-center">
              <div className="mb-4 rounded-full bg-blue-50 p-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  className="w-10 h-10 text-blue-500"
                >
                  <path d="M2.25 6.75A2.25 2.25 0 014.5 4.5h15a2.25 2.25 0 012.25 2.25v10.5A2.25 2.25 0 0119.5 19.5h-15A2.25 2.25 0 012.25 17.25V6.75zM6 9.75a1.5 1.5 0 103 0 1.5 1.5 0 00-3 0z" />
                  <path d="M3 16.5l4.94-4.94a1.5 1.5 0 012.12 0L15 16.5l2.47-2.47a1.5 1.5 0 012.12 0L21 15.34V17.25A2.25 2.25 0 0118.75 19.5h-13.5A2.25 2.25 0 013 17.25v-.75z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-800">
                Chưa có hình ảnh nào
              </h3>
              <p className="text-gray-500 mt-1 max-w-md">
                Thư viện đang trống. Hãy bắt đầu bằng cách tải lên hình ảnh đầu
                tiên của bạn.
              </p>
              <PermissionGuard
                subject={PermissionSubject.IMAGE}
                action="create"
              >
                <Button
                  onClick={() => setOpenUpload(true)}
                  className="mt-4 hover:cursor-pointer"
                >
                  <Plus className="h-4 w-4 mr-2" /> Thêm mới ảnh
                </Button>
              </PermissionGuard>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
              {items.map((img) => (
                <div
                  key={img._id}
                  className="group relative rounded-lg overflow-hidden border border-gray-100 bg-gray-50 hover:shadow-md transition-all"
                >
                  <img
                    src={img.url}
                    alt={img.alt || img.title || ""}
                    className="aspect-square w-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-2 flex justify-between items-end opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="text-white text-xs line-clamp-2">
                      {img.title || img.alt || img.filename}
                    </div>
                    <div className="flex gap-1">
                      <PermissionGuard
                        subject={PermissionSubject.IMAGE}
                        action="update"
                      >
                        <button
                          title="Sao chép URL"
                          onClick={() => navigator.clipboard.writeText(img.url)}
                          className="px-1.5 py-1 rounded bg-white/90 text-gray-800 text-xs hover:bg-white"
                        >
                          URL
                        </button>
                        <button
                          title="Chỉnh sửa"
                          onClick={() => {
                            setSelectedId(img._id);
                            setOpenEdit(true);
                          }}
                          className="px-1.5 py-1 rounded bg-white/90 text-gray-800 text-xs hover:bg-white"
                        >
                          Sửa
                        </button>
                      </PermissionGuard>
                      <PermissionGuard
                        subject={PermissionSubject.IMAGE}
                        action="delete"
                      >
                        <button
                          title="Xóa"
                          onClick={() => {
                            setSelectedId(img._id);
                            setOpenDelete(true);
                          }}
                          className="px-1.5 py-1 rounded bg-red-600 text-white text-xs hover:bg-red-700"
                        >
                          Xóa
                        </button>
                      </PermissionGuard>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Load more */}
        <div className="flex justify-center py-6">
          {pagination.page < pagination.totalPages ? (
            <Button
              onClick={() => fetchMore({ search })}
              variant="outline"
              className="hover:cursor-pointer"
            >
              Tải thêm
            </Button>
          ) : (
            <span className="text-sm text-gray-500">
              Đã hiển thị tất cả ảnh
            </span>
          )}
        </div>
      </div>
      <ModalUploadImage
        isOpen={openUpload}
        onClose={() => setOpenUpload(false)}
        onComplete={() => fetch({ page: 1, limit: 24, search })}
      />
      <ModalEditImage
        isOpen={openEdit}
        onClose={() => setOpenEdit(false)}
        imageId={selectedId}
        onComplete={() =>
          fetch({ page: pagination.page, limit: pagination.limit, search })
        }
      />
      <ModalDeleteImage
        isOpen={openDelete}
        onClose={() => setOpenDelete(false)}
        imageId={selectedId}
        onComplete={() => {}}
      />
    </DashboardLayout>
  );
}
